/**
 * SelectCampaign.js
 *
 * WHAT: Campaign selection dropdown/table for picking an existing campaign to manage.
 * WHY:  Before entering the campaign workflow, users need to select which campaign to work on.
 *       This component lists all campaigns with status, compliance framework, and database type,
 *       and supports deleting (archive or permanent remove) non-completed campaigns.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSafeTimeout } from '../hooks';
import { formatDateTime } from '../utils/dateUtils';

import API_CONFIG from '../config/api.config';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

/**
 * WHAT: Campaign picker component with a sortable table and delete/archive capabilities.
 * WHY:  Provides a single-select interface for navigating into an existing campaign's
 *       workflow, with the option to clean up draft or failed campaigns.
 */
const SelectCampaign = ({ onCampaignSelect }) => {
  const [existingCampaigns, setExistingCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteAction, setDeleteAction] = useState('archive');
  const [statusMessage, setStatusMessage] = useState('');

  // Safe timeout hook for auto-dismissing messages
  const { setSafeTimeout } = useSafeTimeout();

  useEffect(() => {
    checkExistingCampaigns();
  }, []);

  const checkExistingCampaigns = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('campaigns/'));
      if (response.data.success) {
        setExistingCampaigns(response.data.campaigns);
      }
    } catch (error) {
      console.error('Error checking existing campaigns:', error);
      setStatusMessage('❌ Error loading campaigns. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCampaignSelection = (campaignId) => {
    setSelectedCampaign(campaignId);
  };

  const isDeletableCampaign = (campaign) => {
    return campaign.status !== 'COMPLETED';
  };

  const isSelectedCampaignDeletable = () => {
    if (!selectedCampaign) return false;
    const campaign = existingCampaigns.find(c => c.campaign_id === selectedCampaign);
    return campaign && isDeletableCampaign(campaign);
  };

  const showDeleteConfirmation = () => {
    if (!selectedCampaign || !isSelectedCampaignDeletable()) {
      alert('No deletable campaign selected.');
      return;
    }
    setDeleteAction('archive');
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteCampaign = async () => {
    if (!selectedCampaign || !isSelectedCampaignDeletable()) {
      alert('No deletable campaign selected.');
      return;
    }

    const campaign = existingCampaigns.find(c => c.campaign_id === selectedCampaign);
    const campaignName = campaign?.campaign_name;

    setShowDeleteConfirmModal(false);
    setIsLoading(true);

    const actionLabel = deleteAction === 'archive' ? 'archiving' : 'removing';
    setStatusMessage(`Deleting campaign "${campaignName}" (${actionLabel} table)...`);

    try {
      const response = await axios.delete(API_CONFIG.getDatabasesEndpoint('campaigns/delete/'), {
        data: {
          campaign_ids: [selectedCampaign],
          action: deleteAction
        }
      });

      if (response.data.success) {
        const tableInfo = response.data.table_actions && response.data.table_actions.length > 0
          ? ` Table ${deleteAction === 'archive' ? 'archived as ' + response.data.table_actions[0].archived_as : 'removed permanently'}.`
          : '';
        setStatusMessage(`✅ Campaign "${campaignName}" deleted successfully.${tableInfo}`);
        setSelectedCampaign(null);
        await checkExistingCampaigns();
        setSafeTimeout(() => setStatusMessage(''), 5000);
      } else {
        setStatusMessage('❌ Error deleting campaign. Please try again.');
        setSafeTimeout(() => setStatusMessage(''), 5000);
      }
    } catch (error) {
      setStatusMessage('❌ Error deleting campaign. Please check your connection and try again.');
      console.error('Campaign deletion error:', error);
      if (error.response && error.response.data.error) {
        setStatusMessage(`❌ ${error.response.data.error}`);
      }
      setSafeTimeout(() => setStatusMessage(''), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && existingCampaigns.length === 0) {
    return (
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5 className="text-muted">Loading campaigns...</h5>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow-lg border-0">
            <div className="card-header" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '20px'
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-1 fw-bold">
                    <i className="fas fa-list me-2"></i>
                    Manage Existing Campaigns
                  </h3>
                  <p className="mb-0 opacity-75">Select a campaign to view details, delete, or manage workflow</p>
                </div>
                <div>
                  <span className="badge bg-light text-dark" style={{ fontSize: '1.2rem', padding: '10px 20px' }}>
                    {existingCampaigns.length} {existingCampaigns.length === 1 ? 'Campaign' : 'Campaigns'}
                  </span>
                </div>
              </div>
            </div>

            <div className="card-body p-4">
              {/* Status Message */}
              {statusMessage && (
                <div className={`alert ${statusMessage.includes('Error') || statusMessage.includes('❌') ? 'alert-danger' : 'alert-success'} alert-dismissible fade show`} role="alert">
                  <i className={`fas ${statusMessage.includes('Error') || statusMessage.includes('❌') ? 'fa-exclamation-circle' : 'fa-check-circle'} me-2`}></i>
                  {statusMessage}
                  <button type="button" className="btn-close" onClick={() => setStatusMessage('')}></button>
                </div>
              )}

              {/* Action Buttons */}
              {selectedCampaign && (
                <div className="d-flex gap-2 mb-4">
                  <button
                    type="button"
                    className="btn btn-success btn-lg"
                    onClick={() => {
                      const campaign = existingCampaigns.find(c => c.campaign_id === selectedCampaign);
                      if (campaign && onCampaignSelect) {
                        onCampaignSelect(campaign);
                      }
                    }}
                  >
                    <i className="fas fa-play me-2"></i>
                    View Campaign Workflow
                  </button>
                  {isSelectedCampaignDeletable() && (
                    <button
                      type="button"
                      className="btn btn-danger btn-lg"
                      onClick={showDeleteConfirmation}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-trash me-2"></i>
                          Delete Campaign
                        </>
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    onClick={checkExistingCampaigns}
                    disabled={isLoading}
                  >
                    <i className="fas fa-sync me-2"></i>
                    Refresh
                  </button>
                </div>
              )}

              {/* Selected Campaign Info */}
              {selectedCampaign && (
                <div className="alert alert-success border-start border-success border-4 mb-4">
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-check-circle text-success me-2" style={{ fontSize: '1.5rem' }}></i>
                    <h5 className="mb-0">Selected Campaign</h5>
                  </div>
                  <div className="ms-4">
                    <div className="fw-bold fs-5 text-dark mb-2">
                      {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.campaign_name}
                    </div>
                    <div className="d-flex gap-4 flex-wrap">
                      <span>
                        <strong className="text-success">Status:</strong>
                        <span className="text-dark ms-2">
                          {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.status}
                        </span>
                      </span>
                      <span>
                        <strong className="text-info">Compliance:</strong>
                        <span className="text-dark ms-2">
                          {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.compliance_framework}
                        </span>
                      </span>
                      <span>
                        <strong className="text-warning">Database Type:</strong>
                        <span className="text-dark ms-2">
                          {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.database_type}
                        </span>
                      </span>
                    </div>
                    {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.campaign_description && (
                      <div className="mt-2">
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.campaign_description}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Campaigns Table */}
              {existingCampaigns.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover table-bordered">
                    <thead style={{ backgroundColor: '#f8f9fc' }}>
                      <tr>
                        <th style={{ width: '40px' }} className="text-center">
                          <i className="fas fa-check-circle text-success" title="Select Campaign"></i>
                        </th>
                        <th className="fw-semibold">Campaign Name</th>
                        <th className="fw-semibold">Status</th>
                        <th className="fw-semibold">Compliance</th>
                        <th className="fw-semibold">Database Type</th>
                        <th className="fw-semibold">Created By</th>
                        <th className="fw-semibold">Created Date</th>
                        <th className="fw-semibold">Last Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {existingCampaigns.map(campaign => (
                        <tr
                          key={campaign.campaign_id}
                          className={selectedCampaign === campaign.campaign_id ? 'table-primary' : ''}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleCampaignSelection(campaign.campaign_id)}
                        >
                          <td className="text-center">
                            <input
                              type="radio"
                              name="campaignSelection"
                              className="form-check-input"
                              checked={selectedCampaign === campaign.campaign_id}
                              onChange={() => handleCampaignSelection(campaign.campaign_id)}
                              title="Select this campaign"
                            />
                          </td>
                          <td>
                            <div className="fw-semibold text-dark">
                              {campaign.campaign_name}
                            </div>
                            {campaign.campaign_description && (
                              <small className="text-muted d-block mt-1">{campaign.campaign_description}</small>
                            )}
                          </td>
                          <td>
                            <span className="badge" style={{
                              background:
                                campaign.status === 'COMPLETED' ? '#48bb78' :
                                campaign.status === 'ACTIVE' ? '#4299e1' :
                                campaign.status === 'CREATED' ? '#3182ce' :
                                campaign.status === 'ATTESTATION' ? '#ed8936' :
                                campaign.status === 'RUNNING' ? '#3182ce' :
                                campaign.status === 'FAILED' ? '#f56565' :
                                campaign.status === 'PAUSED' ? '#ed8936' :
                                campaign.status === 'CANCELLED' ? '#718096' :
                                '#4299e1',
                              color: '#fff',
                              padding: '6px 16px',
                              borderRadius: '20px',
                              fontSize: '0.85rem',
                              fontWeight: '600'
                            }}>
                              {campaign.status === 'ACTIVE' && <i className="fas fa-play-circle me-1"></i>}
                              {campaign.status === 'COMPLETED' && <i className="fas fa-check-circle me-1"></i>}
                              {campaign.status === 'ATTESTATION' && <i className="fas fa-clipboard-check me-1"></i>}
                              {campaign.status === 'FAILED' && <i className="fas fa-times-circle me-1"></i>}
                              {campaign.status === 'CANCELLED' && <i className="fas fa-ban me-1"></i>}
                              {campaign.status}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-success text-white px-2 py-1">{campaign.compliance_framework}</span>
                          </td>
                          <td>
                            <span className="badge bg-info text-white px-2 py-1">{campaign.database_type}</span>
                          </td>
                          <td>
                            <div className="text-dark">{campaign.created_by}</div>
                          </td>
                          <td>
                            <div className="text-dark">
                              {formatDateTime(campaign.created_date)}
                            </div>
                          </td>
                          <td>
                            {campaign.last_modified_date ? (
                              <div className="text-dark">
                                {formatDateTime(campaign.last_modified_date)}
                              </div>
                            ) : (
                              <span className="text-muted fst-italic">
                                <i className="fas fa-minus-circle me-1"></i>
                                Never
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-inbox fa-4x text-muted mb-4"></i>
                  <h4 className="text-dark fw-bold mb-2">No campaigns found</h4>
                  <p className="text-muted fs-5 mb-3">Get started by creating your first UAR campaign</p>
                  <p className="text-secondary">
                    Navigate to <span className="fw-semibold text-success">"Create New Campaign"</span> to begin
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Delete Campaign - Choose Action
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDeleteConfirmModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Campaign:</strong> {existingCampaigns.find(c => c.campaign_id === selectedCampaign)?.campaign_name}
                </div>
                <p className="mb-4">
                  Please choose what to do with the campaign's report table:
                </p>
                <div className="mb-3">
                  <div className="form-check mb-3 p-3 border rounded" style={{backgroundColor: deleteAction === 'archive' ? '#e8f5e9' : 'transparent'}}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="deleteAction"
                      id="archiveAction"
                      value="archive"
                      checked={deleteAction === 'archive'}
                      onChange={(e) => setDeleteAction(e.target.value)}
                    />
                    <label className="form-check-label w-100" htmlFor="archiveAction">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-archive fa-2x text-success me-3 mt-1"></i>
                        <div>
                          <strong className="d-block mb-1">Archive (Recommended)</strong>
                          <small className="text-muted">
                            Rename the table to <code>TABLE_NAME_ARCHIVE_YYYYMMDD</code>
                            <br />
                            The table will be preserved and can be referenced later if needed.
                          </small>
                        </div>
                      </div>
                    </label>
                  </div>
                  <div className="form-check p-3 border rounded" style={{backgroundColor: deleteAction === 'remove' ? '#ffebee' : 'transparent'}}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="deleteAction"
                      id="removeAction"
                      value="remove"
                      checked={deleteAction === 'remove'}
                      onChange={(e) => setDeleteAction(e.target.value)}
                    />
                    <label className="form-check-label w-100" htmlFor="removeAction">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-trash-alt fa-2x text-danger me-3 mt-1"></i>
                        <div>
                          <strong className="d-block mb-1">Remove Permanently</strong>
                          <small className="text-muted">
                            Drop the table completely from the database
                            <br />
                            <span className="text-danger fw-bold">⚠️ This action cannot be undone!</span>
                          </small>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirmModal(false)}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${deleteAction === 'archive' ? 'btn-warning' : 'btn-danger'}`}
                  onClick={confirmDeleteCampaign}
                >
                  <i className={`fas ${deleteAction === 'archive' ? 'fa-archive' : 'fa-trash'} me-2`}></i>
                  {deleteAction === 'archive' ? 'Archive & Delete Campaign' : 'Remove & Delete Campaign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectCampaign;
